import { useState } from 'react';
import { Input, Button, Dropdown } from 'antd';
import {
  SearchOutlined, PlusOutlined, MoreOutlined, EditOutlined, DeleteOutlined, DownOutlined,
} from '@ant-design/icons';
import CreateGroupModal from '../../components/createGroupModal';
import {
  useCreateLeadGroupMutation,
  useUpdateLeadGroupMutation, useDeleteLeadGroupMutation,
} from '../../services/leadGroups';
import { useGetLeadGroupCountsQuery } from '../../services/leadGroupCounts';
import './styles.scss';

/* ─── Sidebar structure ──────────────────────────────────────────────── */

const SIDEBAR = [
  { key: 'edit-profile', label: 'Edit Profile', standalone: true },
  {
    key: 'settings', label: 'Settings', section: true, items: [
      { key: 'notifications',   label: 'Notifications' },
      { key: 'personalisation', label: 'Personalisation' },
      { key: 'follow-ups',      label: 'Follow Ups' },
      { key: 'uncontacted',     label: 'Uncontacted Leads' },
      { key: 'duplicate',       label: 'Duplicate Leads' },
      { key: 'client-groups',   label: 'Client Groups' },
      { key: 'custom-fields',   label: 'Custom Client Fields' },
      { key: 'fb-pixel',        label: 'Facebook Pixel & Google Tag ID' },
      { key: 'meta-conversions', label: 'Meta Conversions API' },
      { key: 'whatsapp',        label: 'WhatsApp Business' },
    ],
  },
];

/* ─── Client groups data ─────────────────────────────────────────────── */

interface Group {
  id:    string;
  name:  string;
  color: string;
  count: number;
}

/* ─── Component ───────────────────────────────────────────────────────── */

const ClientSettings = () => {
  const [active, setActive] = useState('client-groups');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editGroup, setEditGroup]   = useState<Group | null>(null);
  // Accordion: which sections are expanded (Settings open by default).
  const [expanded, setExpanded] = useState<Record<string, boolean>>({ settings: true });

  const toggleSection = (key: string) =>
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  // ── Client-groups list with counts (GET /lead-groups/counts?search=) ──
  const { data: groupsResp, refetch: refetchGroups } =
    useGetLeadGroupCountsQuery({ search: search || undefined });
  const [createLeadGroup, { isLoading: creating }] = useCreateLeadGroupMutation();
  const [updateLeadGroup, { isLoading: updating }] = useUpdateLeadGroupMutation();
  const [deleteLeadGroup] = useDeleteLeadGroupMutation();

  // Search is server-side, so render the API result directly.
  const filtered: Group[] = ((groupsResp?.data ?? groupsResp ?? []) as any[]).map((g, i) => ({
    id:    g._id ?? g.id ?? `group-${i}`,
    name:  g.name ?? '—',
    color: g.colour ?? g.color ?? '#64748b',
    count: g.leadCount ?? g.clientsAssigned ?? g.count ?? 0,
  }));

  // Handles both create (POST) and edit (PATCH) — edit mode when editGroup is set.
  const handleSaveGroup = async ({ name, color }: { name: string; color: string }) => {
    try {
      if (editGroup) await updateLeadGroup({ id: editGroup.id, name, colour: color }).unwrap();
      else           await createLeadGroup({ name, colour: color }).unwrap();
      refetchGroups();   // counts endpoint is a separate cache — refresh it manually
    } catch {
      /* error surfaced by the global bad-request handler */
    }
  };

  const handleDeleteGroup = async (g: Group) => {
    try {
      await deleteLeadGroup(g.id).unwrap();
      refetchGroups();
    } catch {
      /* error surfaced by the global bad-request handler */
    }
  };

  const rowMenu = (g: Group) => ({
    items: [
      { key: 'edit',   label: 'Edit Group',   icon: <EditOutlined /> },
      { key: 'delete', label: 'Delete Group', icon: <DeleteOutlined />, danger: true },
    ],
    onClick: ({ key }: { key: string }) => {
      if (key === 'edit')   { setEditGroup(g); setCreateOpen(true); }
      if (key === 'delete') handleDeleteGroup(g);
    },
  });

  return (
    <div className="cs-page">
      <h1 className="cs-title">Account</h1>

      <div className="cs-body">
        {/* ── Sidebar ── */}
        <aside className="cs-sidebar">
          {SIDEBAR.map(entry => (
            entry.standalone ? (
              <button
                key={entry.key}
                className={`cs-nav-item standalone ${active === entry.key ? 'active' : ''}`}
                onClick={() => setActive(entry.key)}
              >
                {entry.label}
              </button>
            ) : (
              <div key={entry.key} className={`cs-nav-group ${expanded[entry.key] ? 'open' : ''}`}>
                <button
                  className="cs-nav-heading"
                  onClick={() => toggleSection(entry.key)}
                >
                  <span>{entry.label}</span>
                  <DownOutlined className="cs-nav-caret" />
                </button>
                {expanded[entry.key] && (
                  <div className="cs-nav-sub">
                    {entry.items!.map(sub => (
                      <button
                        key={sub.key}
                        className={`cs-nav-item ${active === sub.key ? 'active' : ''}`}
                        onClick={() => setActive(sub.key)}
                      >
                        {sub.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )
          ))}
        </aside>

        {/* ── Content ── */}
        <section className="cs-content">
          {active === 'client-groups' ? (
            <>
              <h3 className="cs-section-title">CLIENT GROUPS</h3>
              <p className="cs-section-desc">
                Create and use Groups to help manage and organise your clients. Each client can be
                tagged to multiple groups. <a href="#" className="cs-learn-more">Learn More ›</a>
              </p>

              <Button
                type="primary"
                icon={<PlusOutlined />}
                className="cs-create-btn"
                size="large"
                onClick={() => { setEditGroup(null); setCreateOpen(true); }}
              >
                Create new group
              </Button>

              <div className="cs-search">
                <Input
                  size="large"
                  placeholder="Search groups"
                  prefix={<SearchOutlined style={{ color: 'var(--muted)' }} />}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>

              <div className="cs-group-list">
                {filtered.map(g => (
                  <div key={g.id} className="cs-group-item">
                    <span className="cs-group-color" style={{ background: g.color }} />
                    <div className="cs-group-info">
                      <div className="cs-group-name">{g.name}</div>
                      <div className="cs-group-count">Clients Assigned: {g.count}</div>
                    </div>
                    <Dropdown menu={rowMenu(g)} trigger={['click']} placement="bottomRight">
                      <button className="cs-group-menu" onClick={e => e.stopPropagation()}>
                        <MoreOutlined />
                      </button>
                    </Dropdown>
                  </div>
                ))}
                {filtered.length === 0 && <div className="cs-empty">No groups found</div>}
              </div>
            </>
          ) : (
            <div className="cs-placeholder">
              <h3 className="cs-section-title">
                {SIDEBAR.flatMap(s => s.items ?? [{ key: s.key, label: s.label }])
                  .find(i => i.key === active)?.label}
              </h3>
              <p className="cs-section-desc">This section is coming soon.</p>
            </div>
          )}
        </section>
      </div>

      <CreateGroupModal
        open={createOpen}
        onClose={() => { setCreateOpen(false); setEditGroup(null); }}
        onCreate={handleSaveGroup}
        loading={creating || updating}
        initial={editGroup ? { name: editGroup.name, color: editGroup.color } : null}
      />
    </div>
  );
};

export default ClientSettings;
